import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowLeft, Star, CheckCircle, TrendingUp, Shield, MessageCircle, Users, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PremiumPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [banHistory, setBanHistory] = useState([]);
  const isPremium = user.role === 'premium';

  useEffect(() => {
    if (isPremium) {
      loadPremiumData();
    }
  }, [isPremium]);

  const loadPremiumData = async () => {
    try {
      const [statsRes, banHistoryRes] = await Promise.all([
        axios.get(`${API}/premium/stats`),
        axios.get(`${API}/premium/ban-history`)
      ]);
      setStats(statsRes.data);
      setBanHistory(banHistoryRes.data);
    } catch (error) {
      console.error('Failed to load premium data:', error);
    }
  };

  const handleRequestPremium = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/premium/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Premium access request submitted! Please wait for admin approval.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleDowngrade = async () => {
    if (!window.confirm('Are you sure you want to downgrade from Premium?')) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/premium/downgrade`);
      toast.success('Downgraded from Premium');
      
      const updatedUser = { ...user, role: response.data.role };
      setUser(updatedUser);
      
      setStats(null);
      setBanHistory([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to downgrade');
    } finally {
      setLoading(false);
    }
  };

  const premiumFeatures = [
    {
      icon: <Shield className="w-6 h-6 text-amber-500" />,
      title: 'Join Any Private Room',
      description: 'Access all private rooms without needing invite codes'
    },
    {
      icon: <Users className="w-6 h-6 text-amber-500" />,
      title: 'See Who Banned You',
      description: 'View complete ban history with moderator names'
    },
    {
      icon: <MessageCircle className="w-6 h-6 text-amber-500" />,
      title: 'Unlimited PM',
      description: 'Send private messages to staff members'
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-amber-500" />,
      title: 'Advanced Statistics',
      description: 'Track your activity with detailed analytics'
    },
    {
      icon: <Crown className="w-6 h-6 text-amber-500" />,
      title: 'Premium Badge',
      description: 'Show off your premium status with a golden badge'
    },
    {
      icon: <Star className="w-6 h-6 text-amber-500" />,
      title: 'Priority Support',
      description: 'Get faster responses from our support team'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50 p-2 sm:p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 sm:mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {!isPremium ? (
          <>
            {/* Premium Upgrade Section */}
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full mb-4">
                <Star className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent mb-4 px-4" data-testid="premium-title">
                Request Premium Access
              </h1>
              <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8 px-4">Submit a request to unlock exclusive features. Admin approval required.</p>
              
              <Card className="max-w-md mx-auto shadow-2xl border-2 border-amber-200">
                <CardHeader className="bg-gradient-to-br from-amber-100 to-yellow-100">
                  <CardTitle className="text-center text-2xl">Premium Access</CardTitle>
                  <CardDescription className="text-center text-lg font-semibold text-amber-700">
                    Admin Approval Required
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <Button
                    onClick={handleRequestPremium}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white text-lg py-6"
                    data-testid="request-premium-button"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                        Submitting Request...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Request Premium Access
                      </span>
                    )}
                  </Button>
                  <p className="text-center text-sm text-gray-500 mt-3">
                    Your request will be reviewed by an admin
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {premiumFeatures.map((feature, idx) => (
                <Card key={idx} className="hover:shadow-lg transition-shadow border-amber-100" data-testid={`feature-${idx}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-amber-50 rounded-lg">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                        <p className="text-gray-600 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Premium Dashboard */}
            <div className="text-center mb-8">
              <Badge className="mb-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-4 py-2 text-lg">
                <Star className="w-5 h-5 mr-2" />
                Premium Member
              </Badge>
              <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="premium-dashboard-title">
                Welcome, Premium Member!
              </h1>
              <p className="text-gray-600">Enjoy your exclusive benefits</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Statistics Card */}
              {stats && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Your Statistics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4" data-testid="premium-stats">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{stats.total_messages}</p>
                        <p className="text-sm text-gray-600">Total Messages</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{stats.total_rooms}</p>
                        <p className="text-sm text-gray-600">Joined Rooms</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">{stats.total_friends}</p>
                        <p className="text-sm text-gray-600">Friends</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-600">{stats.total_pms}</p>
                        <p className="text-sm text-gray-600">Private Messages</p>
                      </div>
                      <div className="p-4 bg-indigo-50 rounded-lg">
                        <p className="text-2xl font-bold text-indigo-600">{stats.rank}</p>
                        <p className="text-sm text-gray-600">Rank Level</p>
                      </div>
                      <div className="p-4 bg-pink-50 rounded-lg">
                        <p className="text-2xl font-bold text-pink-600">{stats.active_days}</p>
                        <p className="text-sm text-gray-600">Active Days</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ban History Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Ban History
                  </CardTitle>
                  <CardDescription>See who banned you and why</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {banHistory.length > 0 ? (
                      <div className="space-y-3">
                        {banHistory.map((ban, idx) => (
                          <div key={idx} className="p-3 border rounded-lg bg-red-50" data-testid={`ban-history-${idx}`}>
                            <div className="flex justify-between items-start mb-1">
                              <p className="font-semibold text-sm">{ban.room?.name || 'Unknown Room'}</p>
                              <Badge variant="destructive" className="text-xs">Banned</Badge>
                            </div>
                            {ban.banned_by && (
                              <p className="text-xs text-gray-600">Banned by: <span className="font-medium">{ban.banned_by.username}</span></p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">{new Date(ban.banned_at).toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center py-8">
                        <CheckCircle className="w-12 h-12 text-green-500 mb-2" />
                        <p className="text-gray-600">No bans on record!</p>
                        <p className="text-sm text-gray-500">You're a model citizen</p>
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Premium Features Grid */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Your Premium Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {premiumFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                      {feature.icon}
                      <div>
                        <p className="font-medium text-sm">{feature.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Downgrade Button */}
            <div className="text-center">
              <Button
                variant="outline"
                onClick={handleDowngrade}
                disabled={loading}
                data-testid="downgrade-premium-button"
                className="text-gray-600 hover:text-gray-800"
              >
                Downgrade from Premium
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PremiumPage;
